import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  buildBreweryLocationLabelMaps,
  collectBreweryIdsFromBeers,
} from '../utils/brewery/buildBreweryLocationLabelMaps';

export const BREWERY_LOCATION_LABELS_ERROR_MESSAGE =
  'Could not load brewery location labels. Showing brewery names instead.';

/**
 * Loads brewery member labels and group membership for beers shown on Engage BeerCards.
 * @param {Array<{ breweryId?: string, availableBreweryIds?: string[] }>} beers
 * @returns {{
 *   breweryNameById: Record<string, string>,
 *   groupedBreweryIds: Record<string, boolean>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
function useBreweryLocationLabelsForBeers(beers) {
  const breweryIds = useMemo(() => collectBreweryIdsFromBeers(beers), [beers]);
  const breweryIdsKey = breweryIds.join('\u0001');

  const [breweryNameById, setBreweryNameById] = useState({});
  const [groupedBreweryIds, setGroupedBreweryIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (breweryIds.length === 0) {
      return undefined;
    }

    let cancelled = false;

    async function loadBreweryLocationLabels() {
      setLoading(true);
      setError(null);

      try {
        const snapshots = await Promise.all(
          breweryIds.map((breweryId) => getDoc(doc(db, 'breweries', breweryId))),
        );

        if (cancelled) {
          return;
        }

        const maps = buildBreweryLocationLabelMaps(snapshots);
        setBreweryNameById(maps.breweryNameById);
        setGroupedBreweryIds(maps.groupedBreweryIds);
        setLoading(false);
        setError(null);
      } catch (loadError) {
        console.error('Error loading brewery location labels:', loadError);
        if (cancelled) {
          return;
        }
        setBreweryNameById({});
        setGroupedBreweryIds({});
        setLoading(false);
        setError(BREWERY_LOCATION_LABELS_ERROR_MESSAGE);
      }
    }

    loadBreweryLocationLabels();

    return () => {
      cancelled = true;
    };
  // breweryIdsKey serializes breweryIds so fetches run only when the ID set changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- breweryIdsKey replaces breweryIds
  }, [breweryIdsKey]);

  const hasBreweryIds = breweryIds.length > 0;

  return {
    breweryNameById: hasBreweryIds ? breweryNameById : {},
    groupedBreweryIds: hasBreweryIds ? groupedBreweryIds : {},
    loading: hasBreweryIds ? loading : false,
    error: hasBreweryIds ? error : null,
  };
}

export default useBreweryLocationLabelsForBeers;
